import clsx from "clsx";
import { useState } from "react";
import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import type {
  AssistantStep,
  Attachment,
  CompletedQuestion,
  Job,
  PendingQuestionMetadata,
} from "@/lib/types";

import { useCancelJob } from "@/hooks/useSession";
import { formatAttachmentSize } from "@/lib/attachments";
import { useStore } from "@/lib/store";
import { formatRelativeTime } from "@/lib/utils";

import { AssistantSteps } from "./AssistantSteps";
import { BroadcastModal } from "./BroadcastModal";
import { CompletedQuestionBlock } from "./CompletedQuestionBlock";
import { QuestionPrompt } from "./QuestionPrompt";

interface ConversationMessageProps {
  job: Job;
}

type JobMetadata = {
  userName?: string;
  // Slack-specific fields
  channel?: string;
  messageTs?: string;
  threadTs?: string;
  // Question/answer flow
  pendingQuestion?: PendingQuestionMetadata;
  questionHistory?: CompletedQuestion[];
  assistantSteps?: AssistantStep[];
};

/**
 * Build a Slack message URL from metadata
 */
function buildSlackUrl(metadata: JobMetadata | null): string | null {
  if (!metadata?.channel || !metadata?.messageTs) return null;

  // Convert timestamp: "1769718626.134899" -> "p1769718626134899"
  const tsForUrl = `p${metadata.messageTs.replace(".", "")}`;
  let url = `https://app.slack.com/archives/${metadata.channel}/${tsForUrl}`;

  // Add thread_ts query param if in a thread
  if (metadata.threadTs) {
    url += `?thread_ts=${metadata.threadTs}&cid=${metadata.channel}`;
  }

  return url;
}

function isImageAttachment(mimeType: string | null | undefined): boolean {
  return typeof mimeType === "string" && mimeType.toLowerCase().startsWith("image/");
}

function isVideoAttachment(mimeType: string | null | undefined): boolean {
  return typeof mimeType === "string" && mimeType.toLowerCase().startsWith("video/");
}

function renderAttachmentPreview(
  attachment: Attachment,
  borderClassName: string,
): JSX.Element | null {
  if (isImageAttachment(attachment.mimeType)) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 block"
        title={attachment.originalFilename}
      >
        <img
          src={attachment.url}
          alt={attachment.originalFilename}
          loading="lazy"
          className={`max-h-80 w-auto max-w-full rounded border bg-cyber-bg/40 ${borderClassName}`}
        />
      </a>
    );
  }

  if (isVideoAttachment(attachment.mimeType)) {
    return (
      // oxlint-disable-next-line jsx-a11y/media-has-caption
      <video
        controls
        preload="metadata"
        className={`mt-2 max-h-96 w-full max-w-full rounded border bg-black ${borderClassName}`}
      >
        <source src={attachment.url} type={attachment.mimeType} />
      </video>
    );
  }

  return null;
}

export function ConversationMessage({ job }: ConversationMessageProps): JSX.Element {
  const theme = useStore((state) => state.theme);
  const isPending =
    job.status === "pending" || job.status === "processing" || job.status === "awaiting_response";
  const metadata = job.metadata as JobMetadata | null;
  const userName = metadata?.userName ?? job.userEmail?.split("@")[0] ?? "USER";
  const slackUrl = job.source === "slack" ? buildSlackUrl(metadata) : null;

  const [copied, setCopied] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const { mutateAsync: cancelJobMutation, isPending: isCancelling } = useCancelJob(job.threadKey);

  const inputAttachments = job.attachments.filter((attachment) => attachment.direction === "input");
  const outputAttachments = job.attachments.filter(
    (attachment) => attachment.direction === "output",
  );

  async function handleCopy(): Promise<void> {
    if (!job.response) return;
    await navigator.clipboard.writeText(job.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleCancel(): Promise<void> {
    try {
      await cancelJobMutation(job.id);
    } catch (error) {
      console.error(`Failed to cancel job ${job.id}:`, error);
    }
  }

  // Show action buttons only for web source, completed jobs with a response
  const showActions = job.source === "web" && job.status === "completed" && job.response;

  return (
    <div className="space-y-3">
      {/* User message */}
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end">
          <div className="mb-1 text-right text-xs text-cyber-cyan">
            {userName}
            <span className="text-cyber-muted">
              {" via "}
              {slackUrl ? (
                <a
                  href={slackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-cyber-cyan"
                >
                  {job.source}
                </a>
              ) : (
                job.source
              )}
            </span>
            <span className="ml-2 text-cyber-muted">{formatRelativeTime(job.createdAt)}</span>
          </div>
          <div className="chat-bubble break-words rounded-lg border border-cyber-cyan/30 bg-cyber-cyan/10 p-3 text-sm text-cyber-text">
            {job.messageText}
            {inputAttachments.length > 0 && (
              <div className="mt-2 rounded border border-cyber-cyan/30 bg-cyber-cyan/5 px-3 py-2 text-xs">
                <div className="mb-1 font-medium text-cyber-cyan">Attachments</div>
                <div className="space-y-1">
                  {inputAttachments.map((attachment) => (
                    <div key={attachment.id} className="text-cyber-text/90">
                      <span className="font-medium">{attachment.originalFilename}</span>
                      <span className="text-cyber-muted">
                        {" "}
                        ({formatAttachmentSize(attachment.sizeBytes)})
                      </span>
                      <div>
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-cyber-cyan underline"
                          title={attachment.url}
                        >
                          {attachment.url}
                        </a>
                      </div>
                      {renderAttachmentPreview(attachment, "border-cyber-cyan/20")}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent response */}
      {(job.response || isPending || job.errorMessage) && (
        <div className="flex justify-start">
          <div className="max-w-[85%]">
            <div className="mb-1 text-xs text-cyber-magenta">
              MAKO
              {job.processedAt && (
                <span className="ml-2 text-cyber-muted">{formatRelativeTime(job.processedAt)}</span>
              )}
            </div>
            {metadata?.assistantSteps && metadata.assistantSteps.length > 0 && !isPending && (
              <div className="mb-2">
                <AssistantSteps steps={metadata.assistantSteps} />
              </div>
            )}
            {/* Completed Q&A history */}
            {metadata?.questionHistory && metadata.questionHistory.length > 0 && (
              <CompletedQuestionBlock history={metadata.questionHistory} />
            )}

            {/* Show cancelled/unanswered question when the job was cancelled while a question was pending */}
            {!isPending && metadata?.pendingQuestion && (
              <div className="mb-3 opacity-70">
                {/* Partial response the AI produced before the cancelled question */}
                {metadata.pendingQuestion.partialResponse && (
                  <div
                    className={clsx(
                      "prose prose-sm mb-2 max-w-none rounded-lg border border-cyber-border bg-cyber-surface p-3 text-sm text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0",
                      theme === "cyber" && "prose-invert",
                    )}
                  >
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {metadata.pendingQuestion.partialResponse}
                    </Markdown>
                  </div>
                )}
                <div className="rounded-lg border border-cyber-muted/20 bg-cyber-muted/5 px-3 py-2.5 text-xs">
                  <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-cyber-muted">
                    Unanswered question
                  </div>
                  {metadata.pendingQuestion.questions.map((q, qi) => (
                    <div
                      key={qi}
                      className={qi > 0 ? "mt-2 border-t border-cyber-border/30 pt-2" : ""}
                    >
                      {q.header && (
                        <div className="mb-0.5 font-medium text-cyber-text/60">{q.header}</div>
                      )}
                      <div className="text-cyber-muted">{q.question}</div>
                      {q.options.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {q.options.map((opt) => (
                            <span
                              key={opt.label}
                              className="inline-block rounded border border-cyber-border/30 px-2 py-0.5 text-cyber-muted/50"
                            >
                              {opt.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isPending && metadata?.pendingQuestion ? (
              <QuestionPrompt
                jobId={job.id}
                threadKey={job.threadKey}
                questions={metadata.pendingQuestion.questions}
                partialResponse={metadata.pendingQuestion.partialResponse}
                assistantSteps={metadata.assistantSteps}
              />
            ) : isPending ? (
              <div className="chat-bubble animate-neon-pulse rounded-lg border border-cyber-magenta/30 bg-cyber-magenta/10 p-3 text-sm text-cyber-magenta">
                <div className="flex items-center justify-between gap-3">
                  <span>
                    <span className="animate-spin-glow mr-2 inline-block">&#x25D0;</span>
                    {job.status === "processing"
                      ? "Processing..."
                      : job.status === "awaiting_response"
                        ? "Waiting for response..."
                        : "Pending..."}
                  </span>
                  <button
                    onClick={handleCancel}
                    disabled={isCancelling}
                    className="text-xs text-cyber-red transition-all hover:text-cyber-red/80 disabled:cursor-not-allowed disabled:text-cyber-muted"
                  >
                    {isCancelling ? "Cancelling..." : "Cancel"}
                  </button>
                </div>
              </div>
            ) : job.errorMessage ? (
              <div className="chat-bubble rounded-lg border border-cyber-red/30 bg-cyber-red/10 p-3 text-sm text-cyber-red">
                {job.errorMessage}
              </div>
            ) : (
              <div
                className={clsx(
                  "chat-bubble prose prose-sm max-w-none overflow-x-auto rounded-lg border border-cyber-border bg-cyber-surface p-3 text-sm text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0",
                  theme === "cyber" && "prose-invert",
                )}
              >
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    a: ({ href, children, ...props }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                      </a>
                    ),
                  }}
                >
                  {job.response}
                </Markdown>

                {job.status === "completed" && outputAttachments.length > 0 && (
                  <div className="mt-3 rounded border border-cyber-border bg-cyber-bg/40 px-3 py-2 text-xs">
                    <div className="mb-1 font-medium text-cyber-magenta">Attachments</div>
                    <div className="space-y-1">
                      {outputAttachments.map((attachment) => (
                        <div key={attachment.id} className="text-cyber-text/90">
                          <span className="font-medium">{attachment.originalFilename}</span>
                          <span className="text-cyber-muted">
                            {" "}
                            ({formatAttachmentSize(attachment.sizeBytes)})
                          </span>
                          <div>
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="break-all text-cyber-cyan underline"
                              title={attachment.url}
                            >
                              {attachment.url}
                            </a>
                          </div>
                          {renderAttachmentPreview(attachment, "border-cyber-border")}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons for web source responses */}
            {showActions && (
              <div className="mt-2 flex gap-3">
                <button
                  onClick={handleCopy}
                  className="text-xs text-cyber-muted transition-all hover:text-cyber-cyan"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button
                  onClick={() => setShowBroadcast(true)}
                  className="text-xs text-cyber-muted transition-all hover:text-cyber-cyan"
                >
                  Broadcast
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Broadcast modal */}
      {showBroadcast && job.response && (
        <BroadcastModal message={job.response} onClose={() => setShowBroadcast(false)} />
      )}
    </div>
  );
}
